import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { QRCodeCanvas } from "qrcode.react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

// MUSE x Ota TOKYO stamp rally: live numbers, volunteer QR sheets, the daily draw and contact export.
// Backend: /stamp-rally/admin/* (admin only, entries carry contact details).

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const api = async (path, options = {}) => {
  const res = await fetch(`${API_BASE_URL}/stamp-rally/admin${path}`, { headers: headers(), ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || data.message || "Request failed");
  return data.data;
};

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "-";

export default function StampRallyPage() {
  const [rallies, setRallies] = useState([]);
  const [rallyId, setRallyId] = useState(null);
  const [summary, setSummary] = useState([]);
  const [entries, setEntries] = useState([]);
  const [day, setDay] = useState("");
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useNotification();

  const rally = useMemo(() => rallies.find((r) => r.id === rallyId), [rallies, rallyId]);

  const run = useCallback(
    async (fn, okMessage) => {
      setLoading(true);
      try {
        const result = await fn();
        if (okMessage) showSuccess("Done", okMessage);
        return result;
      } catch (e) {
        showError("Error", e.message);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadRallies = useCallback(async () => {
    const list = await run(() => api("/rallies"));
    if (!list) return;
    setRallies(list);
    setRallyId((current) => current || list[0]?.id || null);
  }, [run]);

  const loadRally = useCallback(async () => {
    if (!rallyId) return;
    const [s, e] = await Promise.all([
      run(() => api(`/summary/${rallyId}`)),
      run(() => api(`/entries/${rallyId}${day ? `?day=${day}` : ""}`)),
    ]);
    if (s) setSummary(s);
    if (e) setEntries(e);
  }, [rallyId, day, run]);

  useEffect(() => {
    loadRallies();
  }, [loadRallies]);
  useEffect(() => {
    loadRally();
  }, [loadRally]);

  const patchRally = (body, message) =>
    run(async () => {
      await api(`/rallies/${rallyId}`, { method: "PATCH", body: JSON.stringify(body) });
      await loadRallies();
    }, message);

  const draw = async (istDay) => {
    const winners = await run(
      () => api("/draw", { method: "POST", body: JSON.stringify({ rallyId, istDay }) }),
      "Winners drawn",
    );
    if (winners) loadRally();
  };

  const winnerAction = async (winnerId, action, message) => {
    await run(() => api(`/winners/${winnerId}/${action}`, { method: "POST" }), message);
    loadRally();
  };

  const rotate = async (pointId) => {
    if (!window.confirm("Rotate this QR code? The printed QR stops working immediately.")) return;
    await run(() => api(`/points/${pointId}/rotate-code`, { method: "POST" }), "New code created, reprint this sheet");
    loadRallies();
  };

  const exportCsv = () =>
    run(async () => {
      const res = await fetch(`${API_BASE_URL}/stamp-rally/admin/entries/${rallyId}/export.csv${day ? `?day=${day}` : ""}`, {
        headers: headers(),
      });
      if (!res.ok) throw new Error("Export failed");
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `stamp-rally-${day || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    });

  const days = useMemo(() => [...new Set([...summary.map((s) => s.istDay), ...entries.map((e) => e.istDay)])].sort(), [summary, entries]);
  const winnersPerDay = rally?.config?.winnersPerDay ?? 2;

  return (
    <Box p="md" pos="relative">
      <style>{`@media print { .no-print { display: none !important; } }`}</style>
      <LoadingOverlay visible={loading} />
      <Group justify="space-between" mb="md">
        <Title order={3}>Stamp Rally</Title>
        <Select
          w={320}
          data={rallies.map((r) => ({ value: r.id, label: `${r.title} (${r.audience === "ALL" ? "public" : "admins only"})` }))}
          value={rallyId}
          onChange={setRallyId}
          placeholder="No rally yet. Run scripts/seedStampRally.js"
        />
      </Group>

      {!rally ? (
        <Text c="dimmed">No rally found.</Text>
      ) : (
        <Tabs defaultValue="overview">
          <Tabs.List mb="md">
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="draw">Draw and entries</Tabs.Tab>
            <Tabs.Tab value="panels">Panels and QR sheets</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview">
            <Stack>
              <Paper withBorder p="md">
                <Group justify="space-between" align="flex-end">
                  <Stack gap={4}>
                    <Switch
                      label="Rally is live in the app"
                      checked={rally.isActive}
                      onChange={(e) => patchRally({ isActive: e.currentTarget.checked }, "Updated")}
                    />
                    <Switch
                      label="Visible to everyone (off = admins only, for testing)"
                      checked={rally.audience === "ALL"}
                      onChange={(e) => patchRally({ audience: e.currentTarget.checked ? "ALL" : "ADMIN_ONLY" }, "Updated")}
                    />
                    <Text size="sm" c="dimmed">
                      Runs {fmt(rally.startsAt)} to {fmt(rally.endsAt)}
                    </Text>
                  </Stack>
                  <NumberInput
                    label="Stamps needed to complete"
                    w={200}
                    min={1}
                    max={rally.points.length}
                    value={rally.requiredCount}
                    onChange={(v) => v && v !== rally.requiredCount && patchRally({ requiredCount: Number(v) }, "Updated")}
                  />
                </Group>
              </Paper>

              {summary.length === 0 && <Text c="dimmed">No stamps yet.</Text>}
              {summary.map((d) => (
                <Paper key={d.istDay} withBorder p="md">
                  <Group justify="space-between" mb="xs">
                    <Title order={5}>{d.istDay}</Title>
                    <Group gap="xs">
                      <Badge variant="light">{d.participants} players</Badge>
                      <Badge color="green" variant="light">{d.completions} complete</Badge>
                      <Badge color="grape" variant="light">{d.winners} winners ({d.claimed} claimed)</Badge>
                    </Group>
                  </Group>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Panel</Table.Th>
                        <Table.Th>Anime</Table.Th>
                        <Table.Th>Stamps given</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rally.points.map((p) => (
                        <Table.Tr key={p.id}>
                          <Table.Td>{p.name}</Table.Td>
                          <Table.Td>{p.ipTitle}</Table.Td>
                          <Table.Td>{d.stampsByPoint[p.id] || 0}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              ))}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="draw">
            <Stack>
              <Group>
                <Select
                  label="Day"
                  clearable
                  placeholder="All days"
                  data={days}
                  value={day || null}
                  onChange={(v) => setDay(v || "")}
                />
                <Button mt={24} variant="default" onClick={exportCsv}>Download CSV</Button>
                {day && (
                  <Button mt={24} onClick={() => draw(day)}>
                    Draw {winnersPerDay} winners for {day}
                  </Button>
                )}
              </Group>
              {!day && <Text size="sm" c="dimmed">Pick a day to draw winners. Voided winners free a slot and are not drawn again.</Text>}
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Day</Table.Th>
                    <Table.Th>Completed</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Phone</Table.Th>
                    <Table.Th>Winner</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {entries.map((e) => (
                    <Table.Tr key={e.id}>
                      <Table.Td>{e.istDay}</Table.Td>
                      <Table.Td>{fmt(e.completedAt)}</Table.Td>
                      <Table.Td>{e.contactName || "-"}</Table.Td>
                      <Table.Td>{e.contactEmail || "-"}</Table.Td>
                      <Table.Td>{e.contactPhone || "-"}</Table.Td>
                      <Table.Td>
                        {e.winner ? (
                          <Group gap="xs">
                            {e.winner.voidedAt ? (
                              <Badge color="gray">Voided</Badge>
                            ) : e.winner.claimedAt ? (
                              <Badge color="green">Prize given</Badge>
                            ) : (
                              <Badge color="grape">Winner</Badge>
                            )}
                            {!e.winner.voidedAt && !e.winner.claimedAt && (
                              <Button size="compact-xs" onClick={() => winnerAction(e.winner.id, "claim", "Marked as given")}>
                                Mark given
                              </Button>
                            )}
                            {!e.winner.voidedAt && !e.winner.claimedAt && (
                              <Button size="compact-xs" color="red" variant="light" onClick={() => winnerAction(e.winner.id, "void", "Winner voided")}>
                                Void
                              </Button>
                            )}
                          </Group>
                        ) : (
                          "-"
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {entries.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Text c="dimmed">No completed cards yet.</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="panels">
            <Stack>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Print this page (volunteers hold one sheet per panel). These QR codes are secret: do not post them anywhere.
                </Text>
                <Button variant="default" onClick={() => window.print()}>Print sheets</Button>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                {rally.points.map((p) => (
                  <Card key={p.id} withBorder padding="lg" style={{ breakInside: "avoid" }}>
                    <Stack align="center" gap="xs">
                      <Title order={4} ta="center">{p.name}</Title>
                      <Text size="sm" ta="center" fw={600}>{p.ipTitle}</Text>
                      <QRCodeCanvas value={`OTASTAMP:v1:${p.code}`} size={220} level="M" includeMargin />
                      <Text size="sm" ta="center">Show your story, get your stamp</Text>
                      <Group gap="xs" className="no-print">
                        {!p.isActive && <Badge color="gray">Off</Badge>}
                        <Button size="compact-xs" color="red" variant="light" onClick={() => rotate(p.id)}>
                          Rotate code
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}
    </Box>
  );
}
